import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../utils/supabaseClient';
import { calculateModuleTimeSaved, formatTimeSaved } from '../services/analyticsService';

interface Module {
  id: string;
  title: string;
  description: string;
  module_type: string;
  tags: string[];
  created_at: string;
  created_by: string;
}

const GrowPage: React.FC = () => {
  const { user } = useAuth();
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [latestModules, setLatestModules] = useState<Module[]>([]);
  const [recommendedModules, setRecommendedModules] = useState<Module[]>([]);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [searchKeywords, setSearchKeywords] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch latest modules
      const { data: modules } = await supabase
        .from('modules')
        .select('id, title, description, module_type, tags, created_at, created_by')
        .order('created_at', { ascending: false })
        .limit(6);

      setLatestModules(modules || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Extract meaningful keywords from user input
  const extractKeywords = (text: string): string[] => {
    // Common stop words to filter out
    const stopWords = new Set([
      'i', 'me', 'my', 'we', 'our', 'you', 'your', 'the', 'a', 'an', 'and', 'or', 'but',
      'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'from', 'up', 'about', 'into',
      'through', 'during', 'before', 'after', 'above', 'below', 'between', 'under',
      'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'having',
      'do', 'does', 'did', 'doing', 'would', 'could', 'should', 'might', 'must', 'shall',
      'will', 'can', 'need', 'want', 'get', 'got', 'take', 'make', 'made', 'spend',
      'this', 'that', 'these', 'those', 'it', 'its', 'they', 'them', 'their',
      'what', 'which', 'who', 'whom', 'when', 'where', 'why', 'how',
      'all', 'each', 'every', 'both', 'few', 'more', 'most', 'other', 'some', 'such',
      'no', 'not', 'only', 'own', 'same', 'so', 'than', 'too', 'very', 'just',
      'also', 'now', 'then', 'here', 'there', 'out', 'if', 'because', 'as', 'until',
      'while', 'although', 'though', 'once', 'since', 'unless', 'over', 'again',
      'hours', 'hour', 'week', 'weeks', 'day', 'days', 'time', 'lot', 'lots', 'many', 'much'
    ]);

    // Extract words, convert to lowercase, filter out stop words and short words
    const words = text.toLowerCase()
      .replace(/[^a-zA-Z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter(word => word.length > 2 && !stopWords.has(word));

    // Return unique keywords
    return Array.from(new Set(words));
  };

  // Search for modules matching keywords
  const searchModulesByKeywords = async (keywords: string[]): Promise<Module[]> => {
    if (keywords.length === 0) return [];

    try {
      // Fetch all approved modules
      const { data: allModules } = await supabase
        .from('modules')
        .select('id, title, description, module_type, tags, created_at, created_by')
        .eq('status', 'approved')
        .order('created_at', { ascending: false });

      if (!allModules || allModules.length === 0) {
        // Fallback: fetch without status filter
        const { data: fallbackModules } = await supabase
          .from('modules')
          .select('id, title, description, module_type, tags, created_at, created_by')
          .order('created_at', { ascending: false });
        
        if (!fallbackModules) return [];
        return scoreAndSortModules(fallbackModules, keywords);
      }

      return scoreAndSortModules(allModules, keywords);
    } catch (error) {
      console.error('Error searching modules:', error);
      return [];
    }
  };

  // Score modules based on keyword matches
  const scoreAndSortModules = (modules: Module[], keywords: string[]): Module[] => {
    const scoredModules = modules.map(module => {
      let score = 0;
      const titleLower = module.title.toLowerCase();
      const descLower = module.description.toLowerCase();
      const tagsLower = (module.tags || []).map(t => t.toLowerCase());
      const typeLower = (module.module_type || '').toLowerCase().replace(/_/g, ' ');

      keywords.forEach(keyword => {
        // Title matches (highest weight)
        if (titleLower.includes(keyword)) score += 10;
        // Tag matches (high weight)
        if (tagsLower.some(tag => tag.includes(keyword))) score += 8;
        // Module type matches
        if (typeLower.includes(keyword)) score += 6;
        // Description matches
        if (descLower.includes(keyword)) score += 3;
      });

      return { module, score };
    });

    // Filter modules with score > 0 and sort by score
    return scoredModules
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 6)
      .map(item => item.module);
  };

  const handleSubmitWorkflow = async () => {
    if (!workflowDescription.trim() || !user) return;

    setSubmitting(true);
    try {
      // Extract keywords from the user's input
      const keywords = extractKeywords(workflowDescription);
      setSearchKeywords(keywords);

      // Save to workflow_insights (for Discovery page)
      const { error } = await supabase
        .from('workflow_insights')
        .insert({
          user_id: user.id,
          description: workflowDescription.trim(),
          created_at: new Date().toISOString()
        });

      if (error && error.code !== '42P01') {
        console.error('Error saving workflow insight:', error);
      }

      // Search for matching modules
      const matchingModules = await searchModulesByKeywords(keywords);
      
      if (matchingModules.length > 0) {
        setRecommendedModules(matchingModules);
        setShowRecommendations(true);
      }

      setSubmitted(true);
      setWorkflowDescription('');
      setTimeout(() => setSubmitted(false), 3000);
    } catch (error) {
      console.error('Error submitting workflow:', error);
      // Still show success for UX
      setSubmitted(true);
      setWorkflowDescription('');
      setTimeout(() => setSubmitted(false), 3000);
    } finally {
      setSubmitting(false);
    }
  };

  // Reset to show latest modules
  const clearRecommendations = () => {
    setShowRecommendations(false);
    setRecommendedModules([]);
    setSearchKeywords([]);
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-10">
        {/* Hero Section */}
        <div className="text-center py-6 sm:py-8">
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-teal-600 dark:bg-teal-500 mb-4">
            <svg className="w-7 h-7 sm:w-8 sm:h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 21v-8m0 0c-4 0-7-3-7-8 4 0 7 3 7 8zm0 0c4 0 7-3 7-8-4 0-7 3-7 8z" />
            </svg>
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-zinc-900 dark:text-zinc-100 mb-2">
            Welcome to the Garden
          </h1>
          <p className="text-base sm:text-lg text-zinc-600 dark:text-zinc-400 max-w-2xl mx-auto px-4 sm:px-0">
            Discover AI tools, automations, and workflows that will help you save hours every week.
          </p>
        </div>

        {/* Workflow Input Section */}
        <div className="bg-teal-50 dark:bg-teal-500/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 border border-teal-200 dark:border-teal-500/20">
          <div className="flex items-start gap-3 sm:gap-4 mb-4">
            <div className="p-2 bg-teal-100 dark:bg-teal-500/20 rounded-lg flex-shrink-0">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-teal-600 dark:text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
              </svg>
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-medium text-zinc-900 dark:text-zinc-100">What takes up most of your time?</h2>
              <p className="text-xs sm:text-sm text-zinc-600 dark:text-zinc-400 mt-1">
                Describe your most time-consuming workflows. We'll help you find AI solutions.
              </p>
            </div>
          </div>
          
          <textarea
            value={workflowDescription}
            onChange={(e) => setWorkflowDescription(e.target.value)}
            placeholder="Example: I spend hours every week manually copying data from emails into spreadsheets, then formatting reports for my team..."
            className="w-full h-24 sm:h-28 px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50 resize-none"
          />
          
          <div className="mt-3 sm:mt-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <p className="text-xs text-zinc-500 text-center sm:text-left">
              Your insights help us curate better content for the team
            </p>
            <button
              onClick={handleSubmitWorkflow}
              disabled={!workflowDescription.trim() || submitting}
              className={`px-5 py-2.5 sm:py-2 rounded-lg text-sm font-medium transition-all ${
                submitted
                  ? 'bg-teal-600 text-white'
                  : 'bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-50 disabled:cursor-not-allowed'
              }`}
            >
              {submitting ? 'Submitting...' : submitted ? '✓ Thanks!' : 'Share Insight'}
            </button>
          </div>
        </div>

        {/* Modules Section - Shows Recommendations or Latest */}
        <div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0 mb-4">
            {showRecommendations ? (
              <>
                <div>
                  <h2 className="text-base sm:text-lg font-medium text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
                    <svg className="w-5 h-5 text-teal-600 dark:text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                    </svg>
                    Recommended for You
                  </h2>
                  <p className="text-xs text-zinc-500 mt-1">
                    Based on: {searchKeywords.slice(0, 5).join(', ')}{searchKeywords.length > 5 ? '...' : ''}
                  </p>
                </div>
                <button
                  onClick={clearRecommendations}
                  className="text-xs sm:text-sm text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Show latest
                </button>
              </>
            ) : (
              <>
                <h2 className="text-base sm:text-lg font-medium text-zinc-900 dark:text-zinc-100">Latest in the Garden</h2>
                <Link to="/modules" className="text-xs sm:text-sm text-teal-600 dark:text-teal-400 hover:underline">
                  View all →
                </Link>
              </>
            )}
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white dark:bg-zinc-800 rounded-lg p-4 sm:p-5 border border-zinc-200 dark:border-zinc-700 animate-pulse">
                  <div className="h-4 bg-zinc-200 dark:bg-zinc-700 rounded w-3/4 mb-3"></div>
                  <div className="h-3 bg-zinc-200 dark:bg-zinc-700 rounded w-full mb-2"></div>
                  <div className="h-3 bg-zinc-200 dark:bg-zinc-700 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          ) : showRecommendations && recommendedModules.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {recommendedModules.map((module, index) => (
                <Link
                  key={module.id}
                  to={`/modules/${module.id}`}
                  className="bg-teal-50 dark:bg-teal-500/10 rounded-lg p-4 sm:p-5 border border-teal-200 dark:border-teal-500/20 hover:border-teal-400 dark:hover:border-teal-500/40 transition-all hover-lift group animate-fade-in-up opacity-0"
                  style={{ animationDelay: `${index * 0.05}s`, animationFillMode: 'forwards' }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 text-xs bg-teal-100 dark:bg-teal-500/20 text-teal-700 dark:text-teal-300 rounded flex items-center gap-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      Match
                    </span>
                    {module.module_type && (
                      <span className="px-2 py-0.5 text-xs bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 rounded">
                        {module.module_type.replace(/_/g, ' ')}
                      </span>
                    )}
                  </div>
                  <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors line-clamp-1">
                    {module.title}
                  </h3>
                  <p className="text-xs text-zinc-600 dark:text-zinc-500 mt-1 line-clamp-2">
                    {module.description}
                  </p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="px-2 py-0.5 text-xs bg-teal-100 dark:bg-teal-500/20 text-teal-700 dark:text-teal-400 rounded">
                      ~{formatTimeSaved(calculateModuleTimeSaved(module.module_type || '', module.tags || []))}/wk
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          ) : showRecommendations && recommendedModules.length === 0 ? (
            <div className="bg-zinc-50 dark:bg-zinc-800/50 rounded-lg p-8 text-center border border-zinc-200 dark:border-zinc-700">
              <svg className="w-12 h-12 text-zinc-400 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
              <p className="text-zinc-600 dark:text-zinc-400 mb-2">No exact matches found for your workflow.</p>
              <p className="text-sm text-zinc-500">
                Your insight has been shared with our team - we'll work on finding solutions!
              </p>
              <button
                onClick={clearRecommendations}
                className="mt-4 text-sm text-teal-600 dark:text-teal-400 hover:underline"
              >
                Browse all modules instead →
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {latestModules.map((module, index) => (
                <Link
                  key={module.id}
                  to={`/modules/${module.id}`}
                  className="bg-white dark:bg-zinc-800 rounded-lg p-4 sm:p-5 border border-zinc-200 dark:border-zinc-700 hover:border-teal-400 dark:hover:border-teal-500/50 transition-all hover-lift group animate-fade-in-up opacity-0"
                  style={{ animationDelay: `${index * 0.05}s`, animationFillMode: 'forwards' }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {module.module_type && (
                      <span className="px-2 py-0.5 text-xs bg-zinc-100 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-400 rounded">
                        {module.module_type.replace(/_/g, ' ')}
                      </span>
                    )}
                    <span className="px-2 py-0.5 text-xs bg-teal-100 dark:bg-teal-500/20 text-teal-700 dark:text-teal-400 rounded">
                      ~{formatTimeSaved(calculateModuleTimeSaved(module.module_type || '', module.tags || []))}/wk
                    </span>
                  </div>
                  <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors line-clamp-1">
                    {module.title}
                  </h3>
                  <p className="text-xs text-zinc-600 dark:text-zinc-500 mt-1 line-clamp-2">
                    {module.description}
                  </p>
                  <p className="text-xs text-zinc-400 mt-2">
                    {new Date(module.created_at).toLocaleDateString()}
                  </p>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/modules"
            className="p-5 bg-white dark:bg-zinc-800 rounded-xl border border-zinc-200 dark:border-zinc-700 hover:border-teal-400 dark:hover:border-teal-500/50 transition-all group shadow-sm"
          >
            <svg className="w-8 h-8 text-teal-600 dark:text-teal-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <h3 className="text-base font-medium text-zinc-900 dark:text-zinc-100 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
              Browse Modules
            </h3>
            <p className="text-sm text-zinc-600 dark:text-zinc-500 mt-1">
              Explore AI tools, prompts, and automations
            </p>
          </Link>

          <Link
            to="/create"
            className="p-5 bg-white dark:bg-zinc-800 rounded-xl border border-zinc-200 dark:border-zinc-700 hover:border-teal-400 dark:hover:border-teal-500/50 transition-all group shadow-sm"
          >
            <svg className="w-8 h-8 text-teal-600 dark:text-teal-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            <h3 className="text-base font-medium text-zinc-900 dark:text-zinc-100 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
              Share Knowledge
            </h3>
            <p className="text-sm text-zinc-600 dark:text-zinc-500 mt-1">
              Create a module and earn tokens
            </p>
          </Link>

          <Link
            to="/how-to"
            className="p-5 bg-white dark:bg-zinc-800 rounded-xl border border-zinc-200 dark:border-zinc-700 hover:border-teal-400 dark:hover:border-teal-500/50 transition-all group shadow-sm"
          >
            <svg className="w-8 h-8 text-teal-600 dark:text-teal-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
            </svg>
            <h3 className="text-base font-medium text-zinc-900 dark:text-zinc-100 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
              How-To Guides
            </h3>
            <p className="text-sm text-zinc-600 dark:text-zinc-500 mt-1">
              Step-by-step tutorials and walkthroughs
            </p>
          </Link>
        </div>
      </div>
    </Layout>
  );
};

export default GrowPage;
